import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Minus, Plus, ChevronLeft, RotateCw } from 'lucide-react';
import { api } from '../lib/api';
import { track } from '../lib/tracker';
import { useCart } from '../context/CartContext';
import { useSite } from '../context/SiteContext';
import { useCurrency } from '../context/CurrencyContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { sanitizeHtml } from '../lib/sanitize';
import SEO from '../components/SEO';
import ImageLoop from '../components/ImageLoop';
import StarRating from '../components/StarRating';
import ProductCard from '../components/ProductCard';

export default function ProductDetail() {
    const { slug } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [imgIndex, setImgIndex] = useState(0);
    const [size, setSize] = useState('');
    const [color, setColor] = useState('');
    const [qty, setQty] = useState(1);
    const { addItem, setDrawerOpen } = useCart();
    const { settings } = useSite();
    const { format } = useCurrency();
    const { customer } = useCustomerAuth();
    const [specialRequest, setSpecialRequest] = useState('');
    const [spin, setSpin] = useState(false);
    const [rating, setRating] = useState({ average: 0, count: 0, my_rating: null });
    const [related, setRelated] = useState([]);
    const [savingRating, setSavingRating] = useState(false);

    useEffect(() => {
        setLoading(true);
        setImgIndex(0);
        api.get(`/products/${slug}`).then((r) => {
            setProduct(r.data);
            setSize(r.data.sizes?.[0] || '');
            setColor(r.data.colors?.[0] || '');
            track('product_view', { product_id: r.data.id, product_name: r.data.name, path: `/product/${slug}` });
        }).catch(() => {}).finally(() => setLoading(false));
    }, [slug]);

    useEffect(() => {
        setSpecialRequest('');
        setSpin(false);
        api.get(`/products/${slug}/rating`).then((r) => setRating(r.data)).catch(() => {});
        api.get(`/products/${slug}/related`).then((r) => setRelated(r.data || [])).catch(() => {});
    }, [slug, customer]);

    const submitRating = async (value) => {
        if (!customer) { toast.error('Please sign in to rate this piece.'); return; }
        setSavingRating(true);
        try {
            const r = await api.post(`/products/${slug}/rating`, { value });
            setRating(r.data);
            toast.success('Thanks for rating!');
        } catch { toast.error('Could not save your rating.'); }
        finally { setSavingRating(false); }
    };

    if (loading) return <div className="py-20 text-center eyebrow" style={{ color: 'var(--ink-muted)' }}>Loading...</div>;
    if (!product) return <div className="py-20 text-center font-serif text-3xl" style={{ color: 'var(--brand-burgundy)' }}>Product not found</div>;

    const onAdd = () => {
        addItem({
            product_id: product.id,
            product_slug: product.slug,
            name: product.name,
            price: product.price,
            image: product.images?.[0] || '',
            size,
            color,
            quantity: qty,
            special_request: specialRequest.trim(),
        });
        toast.success(`${product.name} added to your bag.`);
        setSpecialRequest('');
        setDrawerOpen(true);
    };

    const plainDesc = (product.description || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

    return (
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-10 py-10 lg:py-14" data-testid="product-detail-page">
            <SEO
                title={product.meta_title || product.name}
                description={product.meta_description || plainDesc.slice(0, 200)}
                image={product.images?.[0]}
                canonical={`/product/${product.slug}`}
                type="product"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@type': 'Product',
                    name: product.name,
                    description: plainDesc,
                    image: product.images || [],
                    brand: { '@type': 'Brand', name: settings?.brand_name || 'by anfal' },
                    sku: product.id,
                    category: product.category,
                    offers: {
                        '@type': 'Offer',
                        url: `${(settings?.site_url || 'https://byanfal.com').replace(/\/$/, '')}/product/${product.slug}`,
                        priceCurrency: 'SAR',
                        price: product.price,
                        availability: product.stock_status === 'out_of_stock'
                            ? 'https://schema.org/OutOfStock'
                            : 'https://schema.org/InStock',
                        itemCondition: 'https://schema.org/NewCondition',
                    },
                }}
            />
            <Link to="/shop" className="eyebrow inline-flex items-center gap-2 mb-6" style={{ color: 'var(--brand-burgundy)' }}>
                <ChevronLeft className="w-4 h-4" /> Back to Shop
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
                {/* Gallery */}
                <div>
                    <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-[var(--brand-cream)] luxury-card relative" data-testid="product-image-main">
                        {spin && product.images?.length > 1 ? (
                            <ImageLoop images={product.images} playing interval={700} className="w-full h-full" alt={product.name} testId="product-spin-loop" />
                        ) : product.images?.[imgIndex] ? (
                            <img src={product.images[imgIndex]} alt={product.name} className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-[var(--ink-muted)]">No image</div>
                        )}
                        {product.images?.length > 1 && (
                            <button
                                onClick={() => setSpin((s) => !s)}
                                data-testid="product-spin-toggle"
                                className="absolute bottom-3 right-3 inline-flex items-center gap-2 px-3 py-2 rounded-full text-[11px] uppercase tracking-[0.14em] bg-white/90 backdrop-blur border border-[rgba(90,36,50,0.14)] hover:bg-white transition-colors"
                                style={{ color: 'var(--brand-burgundy)' }}
                            >
                                <RotateCw className={`w-3.5 h-3.5 ${spin ? 'animate-spin' : ''}`} /> {spin ? 'Stop' : '360° View'}
                            </button>
                        )}
                    </div>
                    {product.images?.length > 1 && (
                        <div className="mt-4 flex gap-3" data-testid="product-image-thumbs">
                            {product.images.map((img, i) => (
                                <button
                                    key={img || `thumb-${i}`}
                                    onClick={() => { setSpin(false); setImgIndex(i); }}
                                    className="w-20 h-24 rounded-lg overflow-hidden border-2 transition-colors"
                                    style={{ borderColor: (!spin && i === imgIndex) ? 'var(--brand-burgundy)' : 'transparent' }}
                                    aria-label={`Image ${i+1}`}
                                >
                                    <img src={img} alt={`${product.name} ${i+1}`} className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Details */}
                <div>
                    <p className="eyebrow" style={{ color: 'var(--brand-burgundy)' }}>{product.category || 'by anfal'}</p>
                    <h1 className="font-serif text-4xl lg:text-5xl mt-2 leading-[1.05]" style={{ color: 'var(--brand-burgundy)' }} data-testid="product-title">{product.name}</h1>
                    <p className="font-serif text-2xl lg:text-3xl mt-3" data-testid="product-price">{format(product.price)}</p>
                    <div className="mt-2 flex items-center gap-2" data-testid="product-rating-summary">
                        <StarRating value={Math.round(rating.average)} readOnly size={16} />
                        <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                            {rating.count > 0 ? `${rating.average.toFixed(1)} · ${rating.count} rating${rating.count === 1 ? '' : 's'}` : 'No ratings yet'}
                        </span>
                    </div>
                    <hr className="divider-burgundy mt-6" />
                    <div className="prose-luxury mt-6 text-base leading-[1.85]" style={{ color: 'var(--ink-muted)' }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.description) }} />

                    {product.sizes?.length > 0 && (
                        <div className="mt-8" data-testid="product-size-selector">
                            <p className="eyebrow mb-3" style={{ color: 'var(--ink-muted)' }}>Size</p>
                            <div className="flex flex-wrap gap-2">
                                {product.sizes.map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => setSize(s)}
                                        data-testid={`product-size-option-${s}`}
                                        className="px-4 py-2 rounded-lg border text-sm transition-colors"
                                        style={{
                                            borderColor: size === s ? 'var(--brand-burgundy)' : 'rgba(90,36,50,0.18)',
                                            backgroundColor: size === s ? 'var(--brand-burgundy)' : '#fff',
                                            color: size === s ? '#fff' : 'var(--ink)',
                                        }}
                                    >{s}</button>
                                ))}
                            </div>
                        </div>
                    )}

                    {product.colors?.length > 0 && (
                        <div className="mt-6" data-testid="product-color-selector">
                            <p className="eyebrow mb-3" style={{ color: 'var(--ink-muted)' }}>Color</p>
                            <div className="flex flex-wrap gap-2">
                                {product.colors.map((c) => (
                                    <button
                                        key={c}
                                        onClick={() => setColor(c)}
                                        data-testid={`product-color-option-${c.replace(/\s+/g, '-').toLowerCase()}`}
                                        className="px-4 py-2 rounded-lg border text-sm transition-colors"
                                        style={{
                                            borderColor: color === c ? 'var(--brand-burgundy)' : 'rgba(90,36,50,0.18)',
                                            backgroundColor: color === c ? 'var(--brand-burgundy)' : '#fff',
                                            color: color === c ? '#fff' : 'var(--ink)',
                                        }}
                                    >{c}</button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mt-8 flex items-center gap-4">
                        <p className="eyebrow" style={{ color: 'var(--ink-muted)' }}>Qty</p>
                        <div className="flex items-center border border-[rgba(90,36,50,0.18)] rounded-lg bg-white">
                            <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-2" aria-label="Decrease"><Minus className="w-4 h-4" /></button>
                            <span className="px-4" data-testid="product-qty">{qty}</span>
                            <button onClick={() => setQty(qty + 1)} className="p-2" aria-label="Increase"><Plus className="w-4 h-4" /></button>
                        </div>
                    </div>

                    <div className="mt-8" data-testid="product-special-request">
                        <p className="eyebrow mb-2" style={{ color: 'var(--ink-muted)' }}>Special Request <span className="normal-case tracking-normal">(optional)</span></p>
                        <textarea
                            rows={2}
                            value={specialRequest}
                            onChange={(e) => setSpecialRequest(e.target.value)}
                            data-testid="product-special-request-input"
                            placeholder="e.g. custom sizing, monogram initials, sleeve length..."
                            className="input-luxury resize-none w-full"
                            maxLength={500}
                        />
                    </div>

                    <button
                        onClick={onAdd}
                        data-testid="product-add-to-bag-button"
                        className="btn-burgundy mt-6 w-full px-6 py-4 rounded-xl text-xs tracking-[0.24em] uppercase font-medium"
                    >
                        Add to Bag
                    </button>

                    <p className="mt-6 text-xs" style={{ color: 'var(--ink-muted)' }}>
                        Free worldwide shipping on orders over {format(150)}. Hand-finished in our atelier.
                    </p>

                    <div className="mt-8 pt-6 border-t border-[rgba(90,36,50,0.10)]" data-testid="product-rating-block">
                        <p className="eyebrow mb-2" style={{ color: 'var(--brand-burgundy)' }}>Rate this piece</p>
                        {customer ? (
                            <div className="flex items-center gap-3">
                                <StarRating value={rating.my_rating || 0} onChange={submitRating} size={26} testId="product-rate" />
                                <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                                    {savingRating ? 'Saving...' : rating.my_rating ? `Your rating: ${rating.my_rating}★ (tap to change)` : 'Tap a star to rate'}
                                </span>
                            </div>
                        ) : (
                            <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>
                                <Link to="/account" className="underline" style={{ color: 'var(--brand-burgundy)' }} data-testid="product-rate-signin">Sign in</Link> to rate this piece.
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {related.length > 0 && (
                <section className="mt-16 lg:mt-24" data-testid="product-related">
                    <div className="flex items-end justify-between mb-8">
                        <div>
                            <p className="eyebrow" style={{ color: 'var(--brand-burgundy)' }}>You may also like</p>
                            <h2 className="font-serif text-3xl lg:text-4xl mt-1" style={{ color: 'var(--brand-burgundy)' }}>Complete the look</h2>
                        </div>
                        <Link to="/shop" className="eyebrow hidden sm:inline-flex items-center gap-2" style={{ color: 'var(--brand-burgundy)' }}>View all</Link>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
                        {related.slice(0, 4).map((p, i) => (
                            <ProductCard key={p.id} product={p} index={i} />
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
